import { useEffect, useMemo, useState, type ReactNode } from 'react'

import classNames from 'classnames'
import { MapPin } from 'lucide-react'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import { Modal } from 'components/atomic'

import { COUNTRY_CODES } from 'constants/geo/countries'
import { countryDisplayName, locationDisplayName, usRegionDisplayName } from 'constants/geo/regionLabels'
import { US_STATE_CODES, US_TERRITORY_CODES } from 'constants/geo/usStates'
import { locationApi } from 'services/locationService'
import { setUserLocation } from 'store/auth/slice'
import type { User, UserLocation } from 'types/app'
import { formatShortDate, regionErrorMessage } from 'utils/regionTeams'

import { Banner, Btn, FieldError, FieldLabel, Help, MUTED, SwitchRow, TEXT, inputClass } from './ui'

const COUNTRY_OPTIONS = [...COUNTRY_CODES]
  .map((code) => ({ code, name: countryDisplayName(code) }))
  .sort((a, b) => a.name.localeCompare(b.name))
const STATE_OPTIONS = [...US_STATE_CODES]
  .map((code) => ({ code, name: usRegionDisplayName(code) }))
  .sort((a, b) => a.name.localeCompare(b.name))
const TERRITORY_OPTIONS = [...US_TERRITORY_CODES].map((code) => ({ code, name: usRegionDisplayName(code) }))

/** Future cooldown date (ISO) or null when the location can change now. */
export const activeCooldown = (user?: Pick<User, 'location' | 'locationNextChangeAt'> | null): string | null => {
  const next = user?.locationNextChangeAt
  if (!user?.location?.country || !next) return null
  return new Date(next).getTime() > Date.now() ? next : null
}

export type SetLocationModalProps = {
  isOpen: boolean
  onClose: () => void
  user: Pick<User, 'location' | 'locationNextChangeAt'> | null | undefined
  /** Called after a successful save (e.g. to retry a tournament join). */
  onSaved?: (location: UserLocation) => void
  /** Optional context line, e.g. why a tournament needs it. */
  intro?: ReactNode
  /** Label for the save button. */
  saveLabel?: string
}

/**
 * Country + US state picker for Region Teams (PUT /me/location). The location
 * is snapshotted when a player joins a region tournament, and can only change
 * every 30 days (the visibility toggle can change any time).
 */
export const SetLocationModal = ({ isOpen, onClose, user, onSaved, intro, saveLabel = 'Save location' }: SetLocationModalProps) => {
  const dispatch = useDispatch()
  const current = user?.location?.country ? user.location : null
  const cooldown = activeCooldown(user)

  const [country, setCountry] = useState(current?.country || 'US')
  const [state, setState] = useState(current?.state || '')
  const [isPublic, setIsPublic] = useState(current ? current.public !== false : true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldError, setFieldError] = useState<{ country?: string; state?: string }>({})

  useEffect(() => {
    if (!isOpen) return
    setCountry(current?.country || 'US')
    setState(current?.state || '')
    setIsPublic(current ? current.public !== false : true)
    setError(null)
    setFieldError({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const isUS = country === 'US'
  const changed = !current || current.country !== country || (current.state || '') !== (isUS ? state : '')
  const locked = !!cooldown && !!current
  const preview = useMemo(() => locationDisplayName({ country, state: isUS ? state : null }), [country, state, isUS])

  const save = async () => {
    const fe: typeof fieldError = {}
    if (!country) fe.country = 'Pick a country.'
    if (isUS && !state) fe.state = 'Pick your state.'
    setFieldError(fe)
    if (fe.country || fe.state) {
      document.getElementById(fe.country ? 'loc-country' : 'loc-state')?.focus()
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await locationApi.setMyLocation({ country, state: isUS ? state : null, public: isPublic })
      dispatch(setUserLocation({ location: res.location, locationNextChangeAt: res.locationNextChangeAt }))
      toast.success(changed ? `Location saved: ${locationDisplayName(res.location)}` : 'Location visibility updated')
      onSaved?.(res.location)
      onClose()
    } catch (err: any) {
      const code = err?.response?.data?.code
      const message = regionErrorMessage(err, 'Couldn’t save your location. Try again.')
      if (code === 'STATE_REQUIRED' || code === 'INVALID_STATE') setFieldError({ state: message })
      else if (code === 'INVALID_COUNTRY') setFieldError({ country: message })
      else setError(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title="Your location" isOpened={isOpen} onClose={onClose} withCard>
      <form
        className="w-full max-w-[30rem] space-y-5 text-left"
        onSubmit={(e) => {
          e.preventDefault()
          save()
        }}
        noValidate
      >
        <div className="flex items-start gap-3">
          <span aria-hidden className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-btn-fancy-primary-end/15 text-green">
            <MapPin size={18} />
          </span>
          <div className={classNames('text-sm leading-6', MUTED)}>
            {intro ?? 'Region teams tournaments group players by where they live. Pick your country and, in the US, your state.'}
          </div>
        </div>

        {locked && (
          <Banner>
            You can change this again on <span className={classNames('font-semibold', TEXT)}>{formatShortDate(cooldown)}</span>. You can still
            change who sees it.
          </Banner>
        )}

        <div>
          <FieldLabel htmlFor="loc-country">Country</FieldLabel>
          <select
            id="loc-country"
            value={country}
            disabled={locked}
            onChange={(e) => {
              setCountry(e.target.value)
              setFieldError({})
              if (e.target.value !== 'US') setState('')
            }}
            aria-invalid={!!fieldError.country}
            aria-describedby={fieldError.country ? 'loc-country-err' : undefined}
            className={classNames(inputClass(!!fieldError.country), 'disabled:opacity-60')}
          >
            {COUNTRY_OPTIONS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.name}
              </option>
            ))}
          </select>
          <FieldError id="loc-country-err" message={fieldError.country} />
        </div>

        {isUS && (
          <div>
            <FieldLabel htmlFor="loc-state">State</FieldLabel>
            <select
              id="loc-state"
              value={state}
              disabled={locked}
              onChange={(e) => {
                setState(e.target.value)
                setFieldError({})
              }}
              aria-invalid={!!fieldError.state}
              aria-describedby={fieldError.state ? 'loc-state-err' : 'loc-state-help'}
              className={classNames(inputClass(!!fieldError.state), 'disabled:opacity-60')}
            >
              <option value="">Choose your state</option>
              <optgroup label="States">
                {STATE_OPTIONS.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Territories">
                {TERRITORY_OPTIONS.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </optgroup>
            </select>
            {fieldError.state ? (
              <FieldError id="loc-state-err" message={fieldError.state} />
            ) : (
              <Help id="loc-state-help">We only ever show your state, never your city.</Help>
            )}
          </div>
        )}

        {isUS && (
          <SwitchRow
            id="loc-public"
            checked={isPublic}
            onChange={setIsPublic}
            label="Show my state on my profile"
            description="Organizers of tournaments you join always see it."
          />
        )}

        {changed && preview && (!isUS || state) && (
          <p className={classNames('text-sm', MUTED)} aria-live="polite">
            In region tournaments you’ll represent <span className={classNames('font-semibold', TEXT)}>{preview}</span>.
          </p>
        )}

        {!locked && current && changed && (
          <Help>After saving, you won’t be able to change your location for 30 days.</Help>
        )}
        {!current && <Help>You can change your location once every 30 days.</Help>}

        {error && <Banner tone="error">{error}</Banner>}

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:items-center sm:justify-end">
          <Btn tone="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Btn>
          <Btn tone="primary" type="submit" size="md" loading={saving} disabled={locked && !changed && isPublic === (current?.public !== false)}>
            {locked || !changed ? 'Save' : saveLabel}
          </Btn>
        </div>
      </form>
    </Modal>
  )
}
